import {
  Button,
  Divider,
  Footer,
  Form,
  Instructions,
  RequiredMark,
  Root,
  Row,
  Submit,
  TextField,
  ValidationSummary,
} from '../styled';
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Checkbox from '@mui/material/Checkbox';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/pro-light-svg-icons';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import { ConfirmationDialog } from 'shared/components';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Field } from 'shared/containers/form';
import { PasswordField } from 'shared/containers/form';
import { useForm } from 'shared/containers/form';
import PronounSelect from './PronounSelect';
import RoleSelect from './RoleSelect';
import { signOut } from 'shared/auth';
import { useUpdateUser } from 'shared/apollo';
import PasswordInstructions, {
  checkPasswordValidations,
  getPasswordValidations,
  passwordRegExPattern,
  passwordRules,
} from './PasswordInstructions';

import styles from 'shared/auth/ui/style.module.scss';
import { useQueryString } from 'shared/auth/hooks/useQueryString';
import { validateEINFormat } from './validateEIN';
import SignupAddressSection from './SignupAddressSection';

function EinErrorController({ einError, inputRef }) {
  const { setValue } = useForm();
  const prevError = useRef(false);
  useEffect(() => {
    if (einError && !prevError.current) {
      setValue('ein', '');
      const node = inputRef.current;
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        node.focus();
      }
    }
    prevError.current = einError;
  }, [einError, setValue, inputRef]);
  return null;
}

export default function AccountEditor({
  showLoginButton,
  title,
  mode,
  submitButtonTitle,
  width,
  onDirty,
  userAttributes,
  handleSubmit,
  handleEmailVerified,
  onChangePassword,
  extraButtons,
  header,
  BodyComponent = 'div',
  submitFullWidth,
  pwError,
  pwCheck,
  isDrawer = false,
  errorText = '',
  notOnCognito = false,
  updateCompleted = false,
  handleResendInvitation = () => { /* noop */ },
  einError = false,
  onEinErrorClear = () => { /* noop */ },
}) {
  const einInputRef = useRef(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordValidations, setPasswordValidations] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isNonUs, setIsNonUs] = useState(false);
  const [addressValues, setAddressValues] = useState({
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [formValues, setFormValues] = useState({});
  const { run: runUpdate } = useUpdateUser();
  const query = useQueryString();
  const [email] = useState(
    decodeURIComponent(
      (query.get('email') || query.get('username') || '').replace(/ /g, '+'),
    ),
  );
  const [initialValues, setInitialValues] = useState({});

  useEffect(() => {
    const seeded = {
      ...userAttributes,
      email: email || userAttributes?.email,
    };
    setInitialValues(seeded);
    setFormValues(seeded);
  }, [userAttributes, email]);

  const handlePasswordChange = useCallback(
    e => {
      const value = e.target.value;
      setPassword(value);
      setPasswordValidations(getPasswordValidations(value));
    },
    [setPassword, setPasswordValidations],
  );

  const handleConfirmPasswordChange = useCallback(
    e => {
      const value = e.target.value;
      setConfirmPassword(value);
    },
    [setConfirmPassword],
  );

  const handleShowDeleteDialog = () => {
    setDeleteDialog(true);
  };

  const requiredTextFields = useMemo(() => {
    const base = [
      { name: 'given_name', label: 'First Name' },
      { name: 'family_name', label: 'Last Name' },
      { name: 'email', label: 'Email' },
    ];
    if (mode === 'signup') {
      return [
        ...base,
        { name: 'organization', label: 'Organization name' },
        ...(isNonUs ? [] : [{ name: 'ein', label: 'TIN / EIN' }]),
      ];
    }
    return base;
  }, [mode, isNonUs]);

  const requiredAddressFields = useMemo(
    () => [
      { name: 'streetAddress', label: 'Street Address' },
      { name: 'country', label: 'Country' },
      { name: 'city', label: 'City' },
      { name: 'state', label: 'State / Province' },
      { name: 'zipCode', label: 'Zip Code' },
    ],
    [],
  );

  const showPasswordFields = mode === 'new' || mode === 'signup';

  const computeErrors = useCallback(() => {
    const nextErrors = {};
    requiredTextFields.forEach(({ name, label }) => {
      if (!formValues[name]) {
        nextErrors[name] = `${label} is required`;
      }
    });

    if (showPasswordFields) {
      if (
        checkPasswordValidations(passwordValidations, password, confirmPassword)
      ) {
        nextErrors.password = 'Password does not meet the requirements';
      }
      if (password !== confirmPassword) {
        nextErrors.confirm_password = 'Passwords do not match';
      }
    }

    if (mode === 'signup') {
      requiredAddressFields.forEach(({ name, label }) => {
        if (!addressValues[name]) {
          nextErrors[name] = `${label} is required`;
        }
      });

      if (!isNonUs && formValues.ein) {
        const validation = validateEINFormat(formValues.ein);
        if (!validation.isValid) {
          nextErrors.ein = validation.error;
        }
      }

      if (!termsAccepted) {
        nextErrors.terms =
          'You must accept the Terms of Use and Privacy Policy';
      }
    }

    return nextErrors;
  }, [
    requiredTextFields,
    requiredAddressFields,
    formValues,
    addressValues,
    isNonUs,
    mode,
    showPasswordFields,
    passwordValidations,
    password,
    confirmPassword,
    termsAccepted,
  ]);

  const errors = submitted ? computeErrors() : {};
  if (einError) {
    delete errors.ein;
  }
  const errorMessages = Object.values(errors);

  const handleFormError = useCallback(() => {
    setSubmitted(true);
  }, []);

  const onSubmit = useCallback(
    async values => {
      const validationErrors = computeErrors();
      if (Object.keys(validationErrors).length > 0) {
        setSubmitted(true);
        return false;
      }

      handleSubmit({
        ...values,
        ein: isNonUs ? '' : values.ein,
        termsAccepted: mode === 'signup' ? termsAccepted : undefined,
        ...(mode === 'signup' ? addressValues : {}),
      });
    },
    [handleSubmit, mode, termsAccepted, isNonUs, addressValues, computeErrors],
  );

  const handleDeleteUser = () => {
    const userId = userAttributes?.['custom:user_id'];
    runUpdate({ id: userId, enabled: 0 }, async () => {
      signOut();
    });
    setDeleteDialog(false);
  };

  const startResendInvitation = () => {
    handleResendInvitation({
      email: userAttributes.email,
      firstName: userAttributes['given_name'],
      lastName: userAttributes['family_name'],
    });
  };

  const canChangePassword = ['new', 'signup'].indexOf(mode) === -1;
  const style = useMemo(() => (width ? { width } : undefined)); // eslint-disable-line

  const mainStyle = isDrawer
    ? {
        height: '100vh',
        overflowY: 'auto',
        paddingBottom: '20px',
      }
    : {};

  const { push } = useHistory();

  const pushLogin = () => {
    push(`/login?email=${email}`);
  };

  return (
    <Form
      noValidate
      autoComplete="off"
      initialValues={initialValues}
      onSubmit={onSubmit}
      onError={handleFormError}
      onChange={setFormValues}
      onDirty={onDirty}
      style={style}
    >
      {mode === 'signup' && (
        <EinErrorController einError={einError} inputRef={einInputRef} />
      )}
      {header}
      <BodyComponent className="scroller-drawer" style={mainStyle}>
        <Root
          minWidth={isDrawer ? 410 : 0}
          maxWidth={800}
          style={{
            padding: isDrawer ? '20px' : '0px',
            marginBottom: isDrawer ? '12px' : '12px',
          }}
        >
          <div className={styles.label__wrapper}>
            {title && <h1>{title}</h1>}
            {showLoginButton ? (
              <>
                <span className="regular">Already have an account? </span>
                <span onClick={pushLogin} className={styles.link}>
                  Log in.
                </span>
              </>
            ) : (
              <br />
            )}
          </div>
          <Row>
            <label className="half">
              First Name:<RequiredMark>*</RequiredMark>
              <br />
              <Field
                placeholder="First Name"
                name="given_name"
                required
                component={TextField}
                disabled={notOnCognito}
                error={!!errors.given_name}
                helperText={errors.given_name || ''}
              />
            </label>
            <label className="half">
              Last name:<RequiredMark>*</RequiredMark>
              <br />
              <Field
                placeholder="Last Name"
                name="family_name"
                required
                component={TextField}
                disabled={notOnCognito}
                error={!!errors.family_name}
                helperText={errors.family_name || ''}
              />
            </label>
          </Row>
          <br />
          <label>
            Pronouns: <span className="optional">(Optional)</span>
            <br />
            <PronounSelect disabled={notOnCognito} fullWidth={true} />
          </label>
          <br />
          <br />
          <label>
            Email:<RequiredMark>*</RequiredMark>
            <br />
            <Field
              placeholder={`Email Address${
                mode === 'new' ||
                mode === 'signup' ||
                userAttributes.email_verified
                  ? ''
                  : ' (unverified)'
              }`}
              required
              fullWidth
              name="email"
              value={email || undefined}
              component={TextField}
              disabled={!!email || notOnCognito}
              error={!!errors.email}
              helperText={errors.email || ''}
            />
          </label>
          <br />
          <br />
          <label>
            Phone number: <span className="optional">(Optional)</span>
            <br />
            <p className="help-text">
              Phone number must start with a plus (+) sign, followed immediately
              by the country code.
            </p>
            <Field
              placeholder="+15555555555"
              fullWidth
              name="phone_number"
              component={TextField}
              disabled={notOnCognito}
            />
          </label>
          {/*mode !== 'new' && mode !== 'signup' && (!userAttributes.email_verified || userAttributes.email_verified === 'false') &&
          (mode === 'edit'
            ? <span>(can only be verified by logging in this user)</span>
            : <EmailVerification onVerified={handleEmailVerified}/>
        )*/}
          <br />
          <br />
          <label>
            Job Title: <span className="optional">(Optional)</span>
            <br />
            <Field
              placeholder="Job Title"
              name="custom:title"
              component={TextField}
              fullWidth
              disabled={notOnCognito}
            />
          </label>
          <br />
          <br />
          {mode === 'signup' && (
            <>
              <label>
                Organization name:<RequiredMark>*</RequiredMark>
                <br />
                <Field
                  placeholder="Organization name"
                  name="organization"
                  required
                  component={TextField}
                  fullWidth
                  error={!!errors.organization}
                  helperText={errors.organization || ''}
                />
              </label>
              <br />
              <br />
              <label>
                TIN / EIN:{!isNonUs && <RequiredMark>*</RequiredMark>}
                <br />
                <Field
                  name="ein"
                  placeholder="TIN / EIN"
                  required={!isNonUs}
                  component={TextField}
                  disabled={isNonUs}
                  fullWidth
                  inputRef={einInputRef}
                  onChange={onEinErrorClear}
                  error={einError || !!errors.ein}
                  helperText={
                    einError
                      ? 'The provided EIN is already in use'
                      : errors.ein || ''
                  }
                />
              </label>
              <div>
                <i>
                  Taxpayer Identification Number / Employer Identification
                  Number
                </i>
              </div>
              <br />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isNonUs}
                    onChange={e => setIsNonUs(e.target.checked)}
                    name="isNonUs"
                  />
                }
                label="My organization doesn't have an EIN (ex: for non-US based organizations)"
              />
              <br />
              <br />
              <Divider />
              <p className="regular-text">
                Where is your organization located?
              </p>
              <SignupAddressSection
                values={addressValues}
                onChange={setAddressValues}
                submitted={submitted}
                errors={errors}
              />
            </>
          )}
          <br />
          {mode !== 'signup' && (
            <>
              <Row>
                <label className="half">
                  Role:
                  <br />
                  <RoleSelect disabled={notOnCognito} />
                </label>
                <label className="half"></label>
              </Row>
              <p className="help-text">
                As an administrator, you may edit your organization details,
                notification rules, and manage users
              </p>
            </>
          )}
          <br />
          {!notOnCognito && <Divider />}
          {!notOnCognito && canChangePassword && <h2>Change Password</h2>}
          {!notOnCognito && canChangePassword && (
            <>
              <label>
                Old password:
                <br />
                <PasswordField placeholder="Old Password" name="old_password" />
              </label>
              <br />
              <label>
                Password:
                <br />
                <PasswordField
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Password"
                  name="new_password"
                  pattern={passwordRegExPattern}
                  passwordrules={passwordRules}
                  minlength={12}
                  maxlength={60}
                />
              </label>
              <br />
              <label>
                Confirm password:
                <br />
                <PasswordField
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder="Confirm password"
                  name="confirm_password"
                  pattern={passwordRegExPattern}
                  passwordrules={passwordRules}
                  minlength={12}
                  maxlength={60}
                />
              </label>
              <br />
              <PasswordInstructions
                passwordValidations={passwordValidations}
                password={password}
                confirmPassword={confirmPassword}
              />
              {pwError && (
                <Instructions className="error-text">{pwError}</Instructions>
              )}
              {pwCheck && (
                <Instructions className="error-text">{pwCheck}</Instructions>
              )}
            </>
          )}
          {showPasswordFields && (
            <>
              <label>
                Password:<RequiredMark>*</RequiredMark>
                <br />
                <PasswordField
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Password"
                  name="password"
                  pattern={passwordRegExPattern}
                  passwordrules={passwordRules}
                  minlength={12}
                  maxlength={60}
                  error={!!errors.password}
                />
              </label>
              <br />
              <label>
                Confirm password:<RequiredMark>*</RequiredMark>
                <br />
                <PasswordField
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder="Confirm password"
                  name="confirm_password"
                  pattern={passwordRegExPattern}
                  passwordrules={passwordRules}
                  minlength={12}
                  maxlength={60}
                  error={!!errors.confirm_password}
                />
              </label>
              <br />
              {/* Animate this in once password field is focused */}
              <PasswordInstructions
                passwordValidations={passwordValidations}
                password={password}
                confirmPassword={confirmPassword}
              />
            </>
          )}
          {mode === 'signup' && (
            <>
              <br />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={e => setTermsAccepted(e.target.checked)}
                    name="termsAccepted"
                    color="primary"
                  />
                }
                label={
                  <span>
                    I agree to the{' '}
                    <a
                      href="https://www.trueimpact.com/terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Use
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.trueimpact.com/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    <RequiredMark>*</RequiredMark>
                  </span>
                }
              />
            </>
          )}
          {!notOnCognito && <Divider />}
          {notOnCognito && (
            <Chip
              className="error"
              icon={<ErrorOutlineIcon />}
              label="Unverified user, can't be updated until invitation/sign up is completed"
              variant="outlined"
            />
          )}
          {errorText && (
            <Chip
              className="error"
              icon={<ErrorOutlineIcon />}
              label={errorText}
              variant="outlined"
            />
          )}
          {updateCompleted && (
            <Chip
              icon={<CheckCircleOutlineIcon />}
              label={updateCompleted}
              variant="outlined"
              color="primary"
            />
          )}
          {submitted && (
            <ValidationSummary>
              {errorMessages.length > 0 ? (
                <>
                  <span className="summary-title">
                    Please complete the following required fields:
                  </span>
                  {errorMessages.map(message => (
                    <span className="summary-item" key={message}>
                      <FontAwesomeIcon icon={faTimes} className="error" />
                      <span>{message}</span>
                    </span>
                  ))}
                </>
              ) : (
                <span className="summary-item">
                  <FontAwesomeIcon icon={faCheck} className="success" />
                  <span>No issues found, ready to submit.</span>
                </span>
              )}
            </ValidationSummary>
          )}
          {(mode !== 'new' || (mode === 'new' && !updateCompleted)) && (
            <Footer>
              <Submit fullWidth={submitFullWidth}>{submitButtonTitle}</Submit>
              {extraButtons ? <span>&nbsp;&nbsp;&nbsp;</span> : null}
              {extraButtons}
            </Footer>
          )}
          <br />
          {!notOnCognito && mode !== 'new' && mode !== 'signup' && (
            <>
              <Divider />
              <br />
              <Button
                className="full"
                fullWidth
                variant="outlined"
                onClick={handleShowDeleteDialog}
              >
                Delete this User
              </Button>
            </>
          )}
          {notOnCognito && (
            <>
              <Divider />
              <br />
              <Button
                className="full"
                fullWidth
                variant="outlined"
                onClick={startResendInvitation}
              >
                Resend Invitation
              </Button>
            </>
          )}
        </Root>
      </BodyComponent>
      <ConfirmationDialog
        header={'Delete User'}
        openDialog={deleteDialog}
        onCloseDialog={() => setDeleteDialog(false)}
        actionButtonOne={handleDeleteUser}
        actionButtonTwo={() => setDeleteDialog(false)}
        textButtonOne="Delete"
        textButtonTwo="Cancel"
        title={'Please confirm you want to delete this user. This action is permanent.'}
      />
    </Form>
  );
}

//           {mode !== 'new' && mode !== 'signup' && onChangePassword && <ChangePassword onSubmit={onChangePassword} {...{ mode }}/>}
